import * as flexsearch from "flexsearch";

import { serverContext } from "js/config";
import { search as searchApi } from "apis-v2/callables";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import * as Thumbnails from "js/core/models/thumbnails";
import AppController from "js/core/AppController";
import { getEncoder, getAllDocuments, search, fetchCompressedIndexData } from "common/flexsearch";
import { THUMBNAIL_SIZES } from "common/constants";
import { IUserSlidesSearchDocument } from "common/interfaces";

import DataService from "./DataService";

interface UserSlidesDataServiceSearchQuery {
    fullTextSearchQuery?: string,
    fullTextSearchByFields?: string[],
    size?: number,
    presentationId?: string
}

interface UserSlidesDataServiceDataItem {
    id: string,
    index: number,
    presentationId: string,
    presentationName: string,
    workspaceId: string,
    getThumbnailUrl: (showThemeThumbnails: boolean) => Promise<string>
}

class UserSlidesDataService extends DataService<IUserSlidesSearchDocument> {
    protected async _prepareIndex(workspaceId: string) {
        const locale = serverContext.locale ?? "en";
        const { indexConfig, indexFileReadUrl } = await searchApi.getSlidesSearchIndex({ workspaceId, locale });
        const data = await fetchCompressedIndexData(indexFileReadUrl);
        this._index = new flexsearch.Document<IUserSlidesSearchDocument>({
            ...indexConfig,
            encoder: getEncoder(locale)
        });
        data.forEach(({ key, data }) => this._index.import(key, data));
    }

    public async search(query: UserSlidesDataServiceSearchQuery): Promise<UserSlidesDataServiceDataItem[]> {
        await this.ensureIndexPrepared(AppController.workspaceId);

        const response: UserSlidesDataServiceDataItem[] = [];

        let results: IUserSlidesSearchDocument[];

        if (query.fullTextSearchQuery) {
            results = search({
                index: this._index,
                query: query.fullTextSearchQuery,
                fields: query.fullTextSearchByFields
            });
        } else {
            results = getAllDocuments(this._index);
        }

        if (query.presentationId) {
            results = results.filter(doc => doc.presentation.id === query.presentationId);
        }

        results = results.slice(0, query.size ?? UserSlidesDataService.SEARCH_QUERY_SIZE);

        results.forEach(({ slide, presentation }) => {
            response.push({
                id: slide.id,
                index: slide.index,
                presentationId: presentation.id,
                presentationName: presentation.name,
                workspaceId: this._indexPreparedForWorkspaceId,
                getThumbnailUrl: showThemeThumbnails => {
                    if (showThemeThumbnails && ds.selection.presentation && !ds.selection.presentation.get("isDummy")) {
                        return Thumbnails.getSignedUrlAndLoad(slide.id, slide.modifiedAt, ds.selection.presentation.id, THUMBNAIL_SIZES["small"].suffix);
                    }

                    const slideModel = new Slide({ id: slide.id }, { autoLoad: false });
                    return slideModel.getThumbnailUrl(presentation.id, "small", 0, true, slide.modifiedAt);
                }
            });
        });

        return response;
    }
}

export default UserSlidesDataService;
